import { Stage, type OnCenterCallbackProps } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { memo, useCallback, useContext, useLayoutEffect, useRef } from 'react';
import type { Object3D } from 'three';
import RenderingContext from './Scene/RenderingContext';
import { MODEL_VIEWER_PRESENTATION_SETUPS } from './Presentation/Setup';
import { renderReadiness } from './Rendering/browserReadiness';

export interface ModelElementsProps {
    forceUpdate?: string
    scene: Object3D
}

const environmentUrl = new URL('@resources/img/ModelViewer/environment.hdr', import.meta.url);

/**
 * Shared product-model wrapper around Drei Stage.
 *
 * Product components only prepare GLB objects and materials; this component
 * centralizes environment lighting, centering, historical entry behavior, and
 * the hand-off of measured bounds to cached shadows and pedestals.
 */
export default memo(function ModelElements(props: ModelElementsProps) {
    const advance = useThree(state => state.advance);
    const {
        modelRevision,
        presentationSetup,
        reportCenteredModel: reportCenteredModelToCanvas,
        shadowCacheKey,
        viewerMode,
    } = useContext(RenderingContext);
    const websiteMode = viewerMode === 'website';
    const staticRendering = viewerMode === 'render';
    const presentationConfig = MODEL_VIEWER_PRESENTATION_SETUPS[presentationSetup];
    const studioPresentation = presentationSetup === 'studio';
    const countFrames = useRef(0);
    const centeredReportFrame = useRef<number>();

    // Interactive Stage shadows acknowledge readiness here. Static cached
    // shadows publish readiness only after their separate receiver has drawn.
    useLayoutEffect(() => {
        if(staticRendering) return;

        let mounted = true;
        countFrames.current = 0;
        renderReadiness.setModelReady(false);
        if(modelRevision !== undefined) {
            renderReadiness.reset('shadow', modelRevision);
        }

        const countFramesFunc = () => {
            if(!mounted) return;

            requestAnimationFrame(() => {
                if(!mounted) return;

                if(countFrames.current > 1) {
                    renderReadiness.setModelReady(true);
                    if (modelRevision !== undefined) {
                        renderReadiness.acknowledge(
                            'shadow',
                            modelRevision,
                        );
                    }
                } else {
                    advance(0);
                    countFrames.current++
                    countFramesFunc();
                }
            });
        }

        countFramesFunc();

        return () => {
            mounted = false;
        }
    }, [advance, modelRevision, shadowCacheKey, staticRendering]);

    // Stage is the source of truth for centered bounds. Static mode waits two
    // frames so child transforms settle before sizing the external shadow bake.
    const handleCenteredModel = useCallback((centered: OnCenterCallbackProps) => {
        if(!reportCenteredModelToCanvas) return;

        const model = {
            cacheKey: shadowCacheKey,
            depth: centered.depth,
            modelRevision,
            radius: centered.boundingSphere.radius,
            height: centered.height,
            width: centered.width,
        };

        if(!staticRendering) {
            reportCenteredModelToCanvas(model);
            return;
        }

        if(centeredReportFrame.current !== undefined) {
            cancelAnimationFrame(centeredReportFrame.current);
        }

        centeredReportFrame.current = requestAnimationFrame(() => {
            centeredReportFrame.current = requestAnimationFrame(() => {
                centeredReportFrame.current = undefined;
                reportCenteredModelToCanvas(model);
            });
        });
    }, [modelRevision, reportCenteredModelToCanvas, shadowCacheKey, staticRendering]);

    useLayoutEffect(() => {
        return () => {
            if(centeredReportFrame.current !== undefined) {
                cancelAnimationFrame(centeredReportFrame.current);
                centeredReportFrame.current = undefined;
            }
        }
    }, [
        modelRevision,
        props.forceUpdate,
        reportCenteredModelToCanvas,
        shadowCacheKey,
        staticRendering,
    ]);

    const stageKey = websiteMode
        ? props.forceUpdate
        : `${shadowCacheKey}:${props.forceUpdate ?? ''}:${presentationSetup}`;
    // There are three deliberate shadow modes:
    // - Studio disables Stage shadows in favor of the external cached receiver.
    // - Embedded website viewers use the immediate Neutral accumulation.
    // - Modern interactive Neutral uses a temporal accumulation for quality.
    const stageShadows = studioPresentation
        ? false
        : websiteMode
            ? {
                type: 'accumulative' as const,
                alphaTest: 0.2,
                color: 'white',
                normalBias: 0.02,
                opacity: 0.5,
            }
            : staticRendering
                ? true
                : {
                    type: 'accumulative' as const,
                    alphaTest: 0.2,
                    color: 'white',
                    frames: 100,
                    normalBias: 0.02,
                    opacity: 0.5,
                    temporal: true,
                };

    return <Stage
        // Drei's Stage/Center keeps measured bounds for its mounted lifetime.
        // Remount it when geometry identity changes so camera coordinates from
        // the interactive viewer match the independently centered renderbot.
        key={stageKey}
        environment={studioPresentation ? null : {files: environmentUrl.href}}
        intensity={studioPresentation ? 0 : 0.8}
        preset={studioPresentation ? {
            main: [-1, 1.5, 1.25],
            fill: [1, 0.5, 1],
        } : 'rembrandt'}
        shadows={stageShadows}
        center={
            websiteMode
                ? undefined
                : {onCentered: handleCenteredModel}
        }
        adjustCamera={
            websiteMode
                ? false
                : !staticRendering && presentationConfig.framingMargin
        }
    >
        <primitive
            // Mount the complete owned scene so R3F never reparents children
            // away from their skeleton, KHR variant, or disposal root.
            key={props.scene.id}
            dispose={null}
            object={props.scene}
        />
    </Stage>
})
